/**
 * Sticky ATC for custom-bundle PDP.
 * Reuses classic sticky bar chrome; state + click are puppeteered from
 * `dev-bundle-configurator` (`[data-dev-bundle-atc]` + price root).
 */
import { Component } from '@theme/component';
import { observeStickyAtcHeight } from '@theme/dev-sticky-atc-height';

/**
 * @typedef {Object} DevBundleStickyAtcRefs
 * @property {HTMLElement} stickyBar
 * @property {HTMLButtonElement} addToCartButton
 */

/**
 * @extends {Component<DevBundleStickyAtcRefs>}
 */
class DevBundleStickyAtc extends Component {
  requiredRefs = ['stickyBar', 'addToCartButton'];

  /** @type {IntersectionObserver | null} */
  #atcIntersectionObserver = null;

  /** @type {IntersectionObserver | null} */
  #footerObserver = null;

  /** @type {MutationObserver | null} */
  #syncObserver = null;

  /** @type {AbortController} */
  #abort = new AbortController();

  /** @type {boolean} */
  #isStuck = false;

  /** @type {boolean} */
  #hiddenByBottom = false;

  /** @type {HTMLButtonElement | null} */
  #mainAtc = null;

  /** @type {HTMLElement | null} */
  #observeTarget = null;

  /** @type {(() => void) | null} */
  #stopBarHeightPublish = null;

  connectedCallback() {
    super.connectedCallback();
    this.#stopBarHeightPublish = observeStickyAtcHeight(this.refs.stickyBar);
    this.#bindWhenReady();
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    this.#atcIntersectionObserver?.disconnect();
    this.#footerObserver?.disconnect();
    this.#syncObserver?.disconnect();
    this.#stopBarHeightPublish?.();
    this.#stopBarHeightPublish = null;
    this.#abort.abort();
  }

  /**
   * Configurator is a sibling in the same section — wait a tick if needed.
   */
  #bindWhenReady() {
    const main = this.#findMainAtc();
    if (main) {
      this.#init(main);
      return;
    }

    const { signal } = this.#abort;
    const section = this.closest('.shopify-section');
    if (!(section instanceof HTMLElement)) return;

    const mo = new MutationObserver(() => {
      const next = this.#findMainAtc();
      if (!next) return;
      mo.disconnect();
      this.#init(next);
    });
    mo.observe(section, { childList: true, subtree: true });
    signal.addEventListener('abort', () => mo.disconnect(), { once: true });
  }

  /**
   * @param {HTMLButtonElement} mainAtc
   */
  #init(mainAtc) {
    this.#mainAtc = mainAtc;
    this.#observeTarget = mainAtc.closest('[data-dev-bundle-form]') || mainAtc;

    this.#setupIntersectionObservers();
    this.#setupSyncObserver();
    this.#syncFromMain();
  }

  /** @returns {HTMLButtonElement | null} */
  #findMainAtc() {
    const section = this.closest('.shopify-section');
    const root = section instanceof HTMLElement ? section : document;
    const btn = root.querySelector('dev-bundle-configurator [data-dev-bundle-atc]');
    return btn instanceof HTMLButtonElement ? btn : null;
  }

  #setupIntersectionObservers() {
    const target = this.#observeTarget;
    if (!(target instanceof HTMLElement)) return;

    const footer = document.querySelector('footer') ?? document.querySelector('[class*="footer-group"]');
    if (!footer) return;

    this.#atcIntersectionObserver = new IntersectionObserver((entries) => {
      const [entry] = entries;
      if (!entry) return;

      if (!entry.isIntersecting && !this.#isStuck) {
        this.#showStickyBar();
      } else if (entry.isIntersecting && this.#isStuck) {
        this.#hiddenByBottom = false;
        this.#hideStickyBar();
      }
    });

    this.#footerObserver = new IntersectionObserver(
      (entries) => {
        const [entry] = entries;
        if (!entry) return;

        if (entry.isIntersecting && this.#isStuck) {
          this.#hiddenByBottom = true;
          this.#hideStickyBar();
        } else if (!entry.isIntersecting && this.#hiddenByBottom) {
          const rect = target.getBoundingClientRect();
          const buyVisible = rect.top < window.innerHeight && rect.bottom > 0;
          if (!buyVisible) {
            this.#hiddenByBottom = false;
            this.#showStickyBar();
          }
        }
      },
      { rootMargin: '200px 0px 0px 0px' },
    );

    this.#atcIntersectionObserver.observe(target);
    this.#footerObserver.observe(footer);
  }

  #setupSyncObserver() {
    const main = this.#mainAtc;
    if (!main) return;

    const priceRoot = document.querySelector('dev-bundle-configurator [data-dev-bundle-price-root]');
    const nodes = [main];
    if (priceRoot instanceof HTMLElement) nodes.push(priceRoot);

    this.#syncObserver = new MutationObserver(() => {
      this.#syncFromMain();
    });

    for (const node of nodes) {
      this.#syncObserver.observe(node, {
        attributes: true,
        attributeFilter: ['disabled', 'aria-busy', 'data-dev-bundle-atc-disabled', 'hidden'],
        childList: true,
        subtree: true,
        characterData: true,
      });
    }
  }

  #syncFromMain() {
    const main = this.#mainAtc ?? this.#findMainAtc();
    if (!main) return;
    this.#mainAtc = main;

    const stickyBtn = this.refs.addToCartButton;
    const soldOut = main.hasAttribute('data-dev-bundle-atc-disabled');
    const busy = main.getAttribute('aria-busy') === 'true';

    /*
      Match main bundle ATC: stay visible, disable + sold-out label.
      Do not flip data-variant-available — classic CSS hides the whole bar, which
      would leave a gallery peek gap and differs from the in-page ATC.
    */
    this.dataset.variantAvailable = 'true';
    stickyBtn.toggleAttribute('disabled', soldOut || busy);
    stickyBtn.setAttribute('aria-busy', busy ? 'true' : 'false');

    const label = main.querySelector('[data-dev-bundle-atc-label]')?.textContent?.trim() || '';
    if (label) {
      const labelNodes = this.querySelectorAll('[data-dev-bundle-sticky-atc-label]');
      for (const el of labelNodes) {
        el.textContent = label;
      }
      /* textContent paint is EN/live from main — re-scan Weglot leaves (same as bundle titles). */
      const labelHost =
        this.querySelector('[data-weglot-atc-label]') || stickyBtn;
      if (labelHost instanceof HTMLElement) {
        window.NoomaWeglotChrome?.retranslateLeaves?.(labelHost);
      }
    }

    this.#syncPrice();
    this.#syncMedia();
  }

  #syncPrice() {
    const saleSrc = document.querySelector('dev-bundle-configurator [data-dev-bundle-price-sale]');
    const compareWrapSrc = document.querySelector(
      'dev-bundle-configurator [data-dev-bundle-price-compare-wrap]',
    );
    const compareSrc = document.querySelector('dev-bundle-configurator [data-dev-bundle-price-compare]');

    const saleDst = this.querySelector('[data-dev-bundle-sticky-price-sale]');
    const compareWrapDst = this.querySelector('[data-dev-bundle-sticky-price-compare-wrap]');
    const compareDst = this.querySelector('[data-dev-bundle-sticky-price-compare]');

    if (saleDst instanceof HTMLElement && saleSrc instanceof HTMLElement) {
      saleDst.textContent = saleSrc.textContent || '';
      if (saleSrc.hasAttribute('data-on-sale')) {
        saleDst.setAttribute('data-on-sale', 'true');
      } else {
        saleDst.removeAttribute('data-on-sale');
      }
    }

    if (
      compareWrapDst instanceof HTMLElement &&
      compareDst instanceof HTMLElement &&
      compareWrapSrc instanceof HTMLElement &&
      compareSrc instanceof HTMLElement
    ) {
      compareDst.textContent = compareSrc.textContent || '';
      compareWrapDst.toggleAttribute('hidden', compareWrapSrc.hasAttribute('hidden'));
    }
  }

  #syncMedia() {
    const media = this.querySelector('[data-dev-bundle-sticky-media]');
    const img = media?.querySelector('img');
    if (!(img instanceof HTMLImageElement)) return;

    const packshot = document.querySelector(
      '[data-dev-bundle-gallery-packshot][data-dev-bundle-component-index="0"] [data-dev-bundle-gallery-packshot-media] img',
    );
    if (!(packshot instanceof HTMLImageElement)) return;
    const next = packshot.currentSrc || packshot.src;
    if (!next) return;
    if (next && img.src !== next) {
      img.src = next;
      img.removeAttribute('srcset');
    }
  }

  handleAddToCartClick = () => {
    const main = this.#mainAtc ?? this.#findMainAtc();
    if (!(main instanceof HTMLButtonElement)) return;
    if (main.disabled || main.hasAttribute('data-dev-bundle-atc-disabled')) return;
    if (main.getAttribute('aria-busy') === 'true') return;

    main.dataset.puppet = 'true';
    main.click();

    const stickyBtn = this.refs.addToCartButton;
    if (stickyBtn.dataset.added !== 'true') {
      stickyBtn.dataset.added = 'true';
    }
    window.setTimeout(() => {
      stickyBtn.removeAttribute('data-added');
      delete main.dataset.puppet;
    }, 800);
  };

  handleBackToTopClick = () => {
    const reduceMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
    window.scrollTo({ top: 0, behavior: reduceMotion ? 'instant' : 'smooth' });
  };

  #showStickyBar() {
    this.#isStuck = true;
    this.refs.stickyBar.dataset.stuck = 'true';
  }

  #hideStickyBar() {
    this.#isStuck = false;
    this.refs.stickyBar.dataset.stuck = 'false';
  }
}

if (!customElements.get('dev-bundle-sticky-atc')) {
  customElements.define('dev-bundle-sticky-atc', DevBundleStickyAtc);
}
